/**
 * Mock Data — Integrations Redesign
 *
 * Ported from EBTimeEntry for the new table-based integrations UI.
 * Covers all scenarios: connected, error, not_connected, not_configured,
 * active/inactive companies, companies with/without users.
 */

import type {
  CompanyWithIntegrations,
  CompanyUser,
  EmployeeCompanyAccessRow,
} from '@/types/integrations';
import type {
  ExternalInstanceDto,
  AccessProjectsCompanyDto,
  AvailableDatabaseDto,
  PaginationMetadata,
} from '@/services/api';
import { mockResources } from '@/lib/mock-data';
import { isCompanyUserAvailable } from '@/lib/user-filters';

// ---------------------------------------------------------------------------
// Mock external instances / companies / databases (for demo-mode dropdowns)
// ---------------------------------------------------------------------------

const mockExternalInstances: Record<string, ExternalInstanceDto[]> = {
  'people-hr':   [{ id: 'phr-001', name: 'People HR Production', productId: 'people-hr', isActive: true }],
  bamboohr:      [{ id: 'bhr-001', name: 'BambooHR Production',  productId: 'bamboohr',  isActive: true }],
  adp:           [{ id: 'adp-001', name: 'ADP Workforce',        productId: 'adp',       isActive: true }],
  paycircle:     [{ id: 'pc-001',  name: 'Paycircle Instance',   productId: 'paycircle', isActive: true }],
  workday:       [{ id: 'wd-001',  name: 'Workday Tenant',       productId: 'workday',   isActive: true }],
  'access-focalpoint': [{ id: 'fp-001', name: 'FocalPoint Production', productId: 'access-focalpoint', isActive: true }],
};

const mockProjectCompanies: AccessProjectsCompanyDto[] = [
  { id: 'proj-co-1', name: 'Demo Project Company A' },
  { id: 'proj-co-2', name: 'Demo Project Company B' },
];

const mockDatabases: AvailableDatabaseDto[] = [
  { id: 'db-001', label: 'Demo Time DB 1' },
  { id: 'db-002', label: 'Demo Time DB 2' },
];

// ---------------------------------------------------------------------------
// Simulated async delay
// ---------------------------------------------------------------------------
export const delay = (ms = 500) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export const mockCompaniesWithIntegrations: CompanyWithIntegrations[] = [
  {
    id: 'company-1',
    name: 'Acme Corporation',
    code: 'ACME',
    status: 'active',
    financialIntegration: {
      product: 'xero',
      externalInstanceId: 'xero-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T08:30:00Z',
    },
    hrIntegration: {
      product: 'people-hr',
      externalInstanceId: 'phr-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:00:00Z',
    },
    projectIntegration: {
      product: 'access-projects',
      externalInstanceId: 'ap-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:15:00Z',
    },
    plannerIntegration: {
      product: 'access-resource-scheduler',
      externalInstanceId: 'rs-db-001',
      status: 'connected',
      lastSyncAt: '2025-01-15T09:30:00Z',
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 4,
  },
  {
    id: 'company-2',
    name: 'Beta Limited',
    code: 'BETA',
    status: 'active',
    financialIntegration: {
      product: 'access-financials',
      externalInstanceId: 'af-db-002',
      status: 'connected',
      lastSyncAt: '2025-01-14T16:45:00Z',
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: 'access-focalpoint',
      externalInstanceId: 'fp-db-002',
      status: 'connected',
      lastSyncAt: '2025-01-14T17:00:00Z',
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 2,
  },
  {
    id: 'company-3',
    name: 'Delta Enterprises',
    code: 'DELTA',
    status: 'active',
    financialIntegration: {
      product: 'sage',
      externalInstanceId: 'sage-db-003',
      status: 'connected',
      lastSyncAt: '2025-01-13T12:00:00Z',
    },
    hrIntegration: {
      product: 'bamboohr',
      externalInstanceId: 'bhr-db-003',
      status: 'error',
      lastSyncAt: '2025-01-10T08:00:00Z',
    },
    projectIntegration: {
      product: 'access-projects',
      externalInstanceId: 'ap-db-003',
      status: 'not_connected',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: 'access-planner',
      externalInstanceId: 'planner-db-003',
      status: 'not_connected',
      lastSyncAt: null,
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 3,
  },
  {
    id: 'company-4',
    name: 'Epsilon Solutions',
    code: 'EPSILON',
    status: 'active',
    financialIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 0,
  },
  {
    id: 'company-5',
    name: 'Zeta Technologies',
    code: 'ZETA',
    status: 'active',
    financialIntegration: {
      product: 'quickbooks',
      externalInstanceId: 'qb-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:15:00Z',
    },
    hrIntegration: {
      product: 'workday',
      externalInstanceId: 'wd-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:30:00Z',
    },
    projectIntegration: {
      product: 'access-projects',
      externalInstanceId: 'ap-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T07:45:00Z',
    },
    plannerIntegration: {
      product: 'access-planner',
      externalInstanceId: 'planner-db-005',
      status: 'connected',
      lastSyncAt: '2025-01-15T08:00:00Z',
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 6,
  },
  {
    id: 'company-6',
    name: 'Gamma Industries',
    code: 'GAMMA',
    status: 'inactive',
    financialIntegration: {
      product: 'access-dimensions',
      externalInstanceId: 'ad-db-006',
      status: 'not_connected',
      lastSyncAt: null,
    },
    hrIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    projectIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    plannerIntegration: {
      product: null,
      externalInstanceId: null,
      status: 'not_configured',
      lastSyncAt: null,
    },
    timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
    userCount: 1,
  },
];

// ---------------------------------------------------------------------------
// Users per Company
// ---------------------------------------------------------------------------

export const mockCompanyUsers: Record<string, CompanyUser[]> = {
  // IDs match mockResources IDs (1-15, excluding 'unallocated')
  'company-1': [
    { id: '1', name: 'Sarah Johnson',    email: 'sarah.johnson@company.com',    role: 'Senior Developer',  photoUrl: 'https://i.pravatar.cc/150?img=1',  isDefault: false },
    { id: '2', name: 'Michael Chen',     email: 'michael.chen@company.com',     role: 'UI/UX Designer',    photoUrl: 'https://i.pravatar.cc/150?img=12', isDefault: false },
    { id: '3', name: 'Emily Rodriguez',  email: 'emily.rodriguez@company.com',  role: 'Project Manager',   photoUrl: 'https://i.pravatar.cc/150?img=5',  isDefault: false },
    { id: '4', name: 'David Kim',        email: 'david.kim@company.com',        role: 'Backend Developer', photoUrl: 'https://i.pravatar.cc/150?img=13', isDefault: false },
  ],
  'company-2': [
    { id: '1', name: 'Sarah Johnson',    email: 'sarah.johnson@company.com',    role: 'Senior Developer',  photoUrl: 'https://i.pravatar.cc/150?img=1',  isDefault: false },
    { id: '5', name: 'Jessica Williams', email: 'jessica.williams@company.com', role: 'QA Engineer',       photoUrl: 'https://i.pravatar.cc/150?img=9',  isDefault: false },
  ],
  'company-3': [
    { id: '2', name: 'Michael Chen',     email: 'michael.chen@company.com',     role: 'UI/UX Designer',    photoUrl: 'https://i.pravatar.cc/150?img=12', isDefault: false },
    { id: '6', name: 'Robert Taylor',    email: 'robert.taylor@company.com',    role: 'DevOps Engineer',   photoUrl: 'https://i.pravatar.cc/150?img=14', isDefault: false },
    { id: '7', name: 'Amanda Martinez',  email: 'amanda.martinez@company.com',  role: 'Frontend Developer',photoUrl: 'https://i.pravatar.cc/150?img=20', isDefault: false },
  ],
  'company-4': [],
  'company-5': [
    { id: '1',  name: 'Sarah Johnson',   email: 'sarah.johnson@company.com',    role: 'Senior Developer',  photoUrl: 'https://i.pravatar.cc/150?img=1',  isDefault: false },
    { id: '3',  name: 'Emily Rodriguez', email: 'emily.rodriguez@company.com',  role: 'Project Manager',   photoUrl: 'https://i.pravatar.cc/150?img=5',  isDefault: false },
    { id: '8',  name: 'James Anderson',  email: 'james.anderson@company.com',   role: 'Data Analyst',      photoUrl: 'https://i.pravatar.cc/150?img=33', isDefault: false },
    { id: '9',  name: 'Lisa Thompson',   email: 'lisa.thompson@company.com',    role: 'Senior Developer',  photoUrl: 'https://i.pravatar.cc/150?img=25', isDefault: false },
    { id: '10', name: 'Daniel Brown',    email: 'daniel.brown@company.com',     role: 'UI/UX Designer',    photoUrl: 'https://i.pravatar.cc/150?img=52', isDefault: false },
    { id: '11', name: 'Rachel Green',    email: 'rachel.green@company.com',     role: 'Business Analyst',  photoUrl: 'https://i.pravatar.cc/150?img=32', isDefault: false },
  ],
  'company-6': [
    { id: '12', name: 'Chris Wilson',    email: 'chris.wilson@company.com',     role: 'Backend Developer', photoUrl: 'https://i.pravatar.cc/150?img=57', isDefault: false },
  ],
};

// ---------------------------------------------------------------------------
// All users (search pool)
// ---------------------------------------------------------------------------

// Derive the search pool from this product's actual resources (excluding the system 'unallocated' entry)
export const mockAllUsers: CompanyUser[] = mockResources
  .filter((r) => r.id !== 'unallocated')
  .map((r) => ({
    id: r.id,
    name: r.name,
    email: r.email ?? `${r.name.toLowerCase().replace(/\s+/g, '.')}@company.com`,
    role: r.role,
    status: r.status,
    photoUrl: r.photoUrl,
    isDefault: false,
  }));

// ---------------------------------------------------------------------------
// Employee Company Access
// ---------------------------------------------------------------------------

export function getMockEmployeeCompanyAccess(employeeId: string): EmployeeCompanyAccessRow[] {
  const accessMap: Record<string, { companyIds: string[]; defaultCompanyId: string | null }> = {
    'user-1': { companyIds: ['company-1', 'company-2', 'company-5'], defaultCompanyId: 'company-1' },
    'user-2': { companyIds: ['company-1', 'company-3'],              defaultCompanyId: 'company-1' },
    'user-3': { companyIds: ['company-1', 'company-5'],              defaultCompanyId: 'company-5' },
    'user-4': { companyIds: ['company-1'],                           defaultCompanyId: 'company-1' },
    'user-5': { companyIds: ['company-2'],                           defaultCompanyId: 'company-2' },
    'user-6': { companyIds: ['company-3'],                           defaultCompanyId: 'company-3' },
    'user-7': { companyIds: ['company-3'],                           defaultCompanyId: 'company-3' },
    'user-8': { companyIds: ['company-5'],                           defaultCompanyId: 'company-5' },
  };

  const employeeAccess = accessMap[employeeId] ?? { companyIds: [], defaultCompanyId: null };

  return mockCompaniesWithIntegrations.map((company) => ({
    companyId: company.id,
    companyName: company.name,
    companyCode: company.code,
    companyStatus: company.status,
    hasAccess: employeeAccess.companyIds.includes(company.id),
    isDefault: company.id === employeeAccess.defaultCompanyId,
  }));
}

// ---------------------------------------------------------------------------
// Mutable in-memory store
// ---------------------------------------------------------------------------

let _companyUsersStore: Record<string, CompanyUser[]> = JSON.parse(
  JSON.stringify(mockCompanyUsers)
);

let _companiesStore: CompanyWithIntegrations[] = mockCompaniesWithIntegrations.map((c) => ({ ...c }));

export function resetMockStores() {
  _companyUsersStore = JSON.parse(JSON.stringify(mockCompanyUsers));
  _companiesStore = mockCompaniesWithIntegrations.map((c) => ({ ...c }));
}

function toPagedUsers(
  users: CompanyUser[],
  page: number,
  pageSize: number
): { items: CompanyUser[]; pagination: PaginationMetadata } {
  const safePageSize = Math.max(1, pageSize);
  const totalItems = users.length;
  const totalPages = Math.ceil(totalItems / safePageSize);
  const currentPage = Math.max(1, Math.min(page, totalPages || 1));
  const startIndex = (currentPage - 1) * safePageSize;
  return {
    items: users.slice(startIndex, startIndex + safePageSize),
    pagination: {
      currentPage,
      pageSize: safePageSize,
      totalItems,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      startIndex,
      endIndex: Math.min(startIndex + safePageSize, totalItems),
    },
  };
}

// ---------------------------------------------------------------------------
// Mock Service
// ---------------------------------------------------------------------------

export const mockIntegrationsService = {
  async getCompanies(): Promise<CompanyWithIntegrations[]> {
    await delay(600);
    return [..._companiesStore];
  },

  async getCompany(id: string): Promise<CompanyWithIntegrations | undefined> {
    await delay(300);
    return _companiesStore.find((c) => c.id === id);
  },

  async createCompany(data: {
    name: string;
    code: string;
    status: 'active' | 'inactive';
  }): Promise<CompanyWithIntegrations> {
    await delay(600);
    const newCompany: CompanyWithIntegrations = {
      id: `company-${Date.now()}`,
      name: data.name,
      code: data.code.toUpperCase(),
      status: data.status,
      financialIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      hrIntegration:        { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      projectIntegration:   { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      plannerIntegration:   { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      timesheetIntegration: { product: null, externalInstanceId: null, status: 'not_configured', lastSyncAt: null },
      userCount: 0,
    };
    _companiesStore.push(newCompany);
    _companyUsersStore[newCompany.id] = [];
    return newCompany;
  },

  async updateCompany(
    id: string,
    data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const index = _companiesStore.findIndex((c) => c.id === id);
    if (index === -1) return undefined;
    _companiesStore[index] = { ..._companiesStore[index], ...data };
    return _companiesStore[index];
  },

  async updateIntegrations(
    companyId: string,
    financial: { product: string | null; externalInstanceId: string | null },
    hr: { product: string | null; externalInstanceId: string | null },
    project: { product: string | null; externalInstanceId: string | null },
    planner: { product: string | null; externalInstanceId: string | null },
    timesheet?: { product: string | null; externalInstanceId: string | null }
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(500);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return undefined;

    _companiesStore[index] = {
      ..._companiesStore[index],
      financialIntegration: {
        ..._companiesStore[index].financialIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: financial.product as any,
        externalInstanceId: financial.externalInstanceId,
        status: financial.product && financial.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      hrIntegration: {
        ..._companiesStore[index].hrIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: hr.product as any,
        externalInstanceId: hr.externalInstanceId,
        status: hr.product && hr.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      projectIntegration: {
        ..._companiesStore[index].projectIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: project.product as any,
        externalInstanceId: project.externalInstanceId,
        status: project.product && project.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      plannerIntegration: {
        ..._companiesStore[index].plannerIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: planner.product as any,
        externalInstanceId: planner.externalInstanceId,
        status: planner.product && planner.externalInstanceId ? 'not_connected' : 'not_configured',
      },
      timesheetIntegration: {
        ..._companiesStore[index].timesheetIntegration,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        product: timesheet?.product as any ?? _companiesStore[index].timesheetIntegration.product,
        externalInstanceId: timesheet?.externalInstanceId ?? _companiesStore[index].timesheetIntegration.externalInstanceId,
        status: timesheet?.product && timesheet?.externalInstanceId ? 'not_connected'
          : timesheet ? 'not_configured'
          : _companiesStore[index].timesheetIntegration.status,
      },
    };
    return _companiesStore[index];
  },

  async removeIntegration(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
  ): Promise<CompanyWithIntegrations | undefined> {
    await delay(400);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return undefined;

    const cleared = { product: null, externalInstanceId: null, status: 'not_configured' as const, lastSyncAt: null };

    if (type === 'financial') {
      _companiesStore[index].financialIntegration = cleared;
    } else if (type === 'hr') {
      _companiesStore[index].hrIntegration = cleared;
    } else if (type === 'project') {
      _companiesStore[index].projectIntegration = cleared;
    } else if (type === 'timesheet') {
      _companiesStore[index].timesheetIntegration = cleared;
    } else {
      _companiesStore[index].plannerIntegration = cleared;
    }

    return _companiesStore[index];
  },

  async testConnection(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
  ): Promise<{ success: boolean; message: string }> {
    await delay(1500);
    const company = _companiesStore.find((c) => c.id === companyId);
    if (!company) return { success: false, message: 'Company not found' };

    const integration =
      type === 'financial'
        ? company.financialIntegration
        : type === 'hr'
          ? company.hrIntegration
          : type === 'project'
            ? company.projectIntegration
            : type === 'timesheet'
              ? company.timesheetIntegration
              : company.plannerIntegration;

    if (!integration.product || !integration.externalInstanceId) {
      return { success: false, message: 'Integration not configured' };
    }

    const success = Math.random() > 0.2;

    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index !== -1) {
      const key = type === 'financial' ? 'financialIntegration'
        : type === 'hr' ? 'hrIntegration'
        : type === 'project' ? 'projectIntegration'
        : type === 'timesheet' ? 'timesheetIntegration'
        : 'plannerIntegration';
      _companiesStore[index][key] = {
        ..._companiesStore[index][key],
        status: success ? 'connected' : 'error',
        lastSyncAt: success ? new Date().toISOString() : _companiesStore[index][key].lastSyncAt,
      };
    }

    return {
      success,
      message: success ? 'Connection successful' : 'Connection failed — check your instance configuration',
    };
  },

  async syncIntegration(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
  ): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
    await delay(2000);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return { success: false, message: 'Company not found', syncedAt: null, updatedCompany: undefined };

    const success = Math.random() > 0.1;
    const syncedAt = success ? new Date().toISOString() : null;

    const key = type === 'financial' ? 'financialIntegration'
      : type === 'hr' ? 'hrIntegration'
      : type === 'project' ? 'projectIntegration'
      : type === 'timesheet' ? 'timesheetIntegration'
      : 'plannerIntegration';

    _companiesStore[index][key] = {
      ..._companiesStore[index][key],
      lastSyncAt: success ? syncedAt : _companiesStore[index][key].lastSyncAt,
    };

    return {
      success,
      message: success
        ? 'Sync completed successfully'
        : 'Sync failed — please try again or check your connection settings',
      syncedAt,
      updatedCompany: _companiesStore[index],
    };
  },

  async getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
    await delay(400);
    return [...(_companyUsersStore[companyId] ?? [])];
  },

  async getCompanyUsersPaged(
    companyId: string,
    page: number,
    pageSize: number,
    search = '',
    _signal?: AbortSignal
  ): Promise<{ items: CompanyUser[]; pagination: PaginationMetadata }> {
    await delay(400);
    const query = search.trim().toLowerCase();
    const filtered = (_companyUsersStore[companyId] ?? []).filter(
      (user) =>
        !query ||
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
    );
    return toPagedUsers(filtered, page, pageSize);
  },

  async addUserToCompany(companyId: string, userId: string): Promise<CompanyUser | undefined> {
    await delay(400);
    const user = mockAllUsers.find((u) => u.id === userId);
    if (!user) return undefined;

    const current = _companyUsersStore[companyId] ?? [];
    if (current.some((u) => u.id === userId)) return user;

    _companyUsersStore[companyId] = [...current, user];

    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }

    return user;
  },

  async removeUserFromCompany(companyId: string, userId: string): Promise<void> {
    await delay(400);
    const current = _companyUsersStore[companyId] ?? [];
    _companyUsersStore[companyId] = current.filter((u) => u.id !== userId);

    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }
  },

  async addUsersToCompany(
    companyId: string,
    userIds: string[],
    onProgress?: (processed: number, total: number) => void
  ): Promise<CompanyUser[]> {
    await delay(400);
    if (userIds.length === 0) return [];
    onProgress?.(userIds.length, userIds.length);

    const current = _companyUsersStore[companyId] ?? [];
    const existingIds = new Set(current.map((u) => u.id));
    const toAdd = mockAllUsers.filter(
      (u) => userIds.includes(u.id) && !existingIds.has(u.id)
    );
    _companyUsersStore[companyId] = [...current, ...toAdd];

    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }

    return toAdd;
  },

  async removeUsersFromCompany(
    companyId: string,
    userIds: string[],
    onProgress?: (processed: number, total: number) => void
  ): Promise<void> {
    await delay(400);
    if (userIds.length === 0) return;
    onProgress?.(userIds.length, userIds.length);

    const removeSet = new Set(userIds);
    const current = _companyUsersStore[companyId] ?? [];
    _companyUsersStore[companyId] = current.filter((u) => !removeSet.has(u.id));

    const companyIndex = _companiesStore.findIndex((c) => c.id === companyId);
    if (companyIndex !== -1) {
      _companiesStore[companyIndex] = {
        ..._companiesStore[companyIndex],
        userCount: _companyUsersStore[companyId].length,
      };
    }
  },

  async searchUsers(query: string, excludeCompanyId?: string): Promise<CompanyUser[]> {
    await delay(250);
    const q = query.toLowerCase();
    const excluded = excludeCompanyId ? (_companyUsersStore[excludeCompanyId] ?? []).map((u) => u.id) : [];
    return mockAllUsers.filter(
      (u) =>
        isCompanyUserAvailable(u) &&
        !excluded.includes(u.id) &&
        (u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q))
    );
  },

  async searchUsersPaged(
    query: string,
    excludeCompanyId: string,
    page: number,
    pageSize: number,
    _signal?: AbortSignal
  ): Promise<{ items: CompanyUser[]; pagination: PaginationMetadata }> {
    await delay(250);
    const q = query.trim().toLowerCase();
    const excluded = (_companyUsersStore[excludeCompanyId] ?? []).map((u) => u.id);
    const filtered = mockAllUsers.filter(
      (user) =>
        isCompanyUserAvailable(user) &&
        !excluded.includes(user.id) &&
        (!q ||
          user.name.toLowerCase().includes(q) ||
          user.email.toLowerCase().includes(q) ||
          user.role.toLowerCase().includes(q))
    );
    return toPagedUsers(filtered, page, pageSize);
  },

  async getEmployeeCompanyAccess(employeeId: string): Promise<EmployeeCompanyAccessRow[]> {
    await delay(400);
    return getMockEmployeeCompanyAccess(employeeId);
  },

  async updateEmployeeCompanyAccess(
    employeeId: string,
    updates: Array<{ companyId: string; hasAccess: boolean; isDefault: boolean }>
  ): Promise<EmployeeCompanyAccessRow[]> {
    await delay(500);
    return _companiesStore.map((company) => {
      const update = updates.find((u) => u.companyId === company.id);
      if (update) {
        return {
          companyId: company.id,
          companyName: company.name,
          companyCode: company.code,
          companyStatus: company.status,
          hasAccess: update.hasAccess,
          isDefault: update.isDefault,
        };
      }
      const original = getMockEmployeeCompanyAccess(employeeId).find(
        (r) => r.companyId === company.id
      );
      return original ?? {
        companyId: company.id,
        companyName: company.name,
        companyCode: company.code,
        companyStatus: company.status,
        hasAccess: false,
        isDefault: false,
      };
    });
  },

  // -----------------------------------------------------------------------
  // Demo-mode dropdown data (external instances, project companies, databases)
  // -----------------------------------------------------------------------

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getExternalInstances(productCode: string, _companyId?: string): Promise<ExternalInstanceDto[]> {
    await delay(300);
    return mockExternalInstances[productCode] ?? [];
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getAccessProjectsCompanies(_opts: { product: string }): Promise<AccessProjectsCompanyDto[]> {
    await delay(300);
    return mockProjectCompanies;
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getAvailableDatabases(_productCode: string): Promise<AvailableDatabaseDto[]> {
    await delay(300);
    return mockDatabases;
  },

  async resyncIntegration(
    companyId: string,
    type: 'financial' | 'hr' | 'project' | 'planner' | 'timesheet'
  ): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
    await delay(2500);
    const index = _companiesStore.findIndex((c) => c.id === companyId);
    if (index === -1) return { success: false, message: 'Company not found', syncedAt: null, updatedCompany: undefined };

    const success = Math.random() > 0.1;
    const syncedAt = success ? new Date().toISOString() : null;

    const key = type === 'financial' ? 'financialIntegration'
      : type === 'hr' ? 'hrIntegration'
      : type === 'project' ? 'projectIntegration'
      : type === 'timesheet' ? 'timesheetIntegration'
      : 'plannerIntegration';

    _companiesStore[index][key] = {
      ..._companiesStore[index][key],
      lastSyncAt: success ? syncedAt : _companiesStore[index][key].lastSyncAt,
    };

    return {
      success,
      message: success ? 'Full re-sync completed' : 'Re-sync failed — please try again',
      syncedAt,
      updatedCompany: _companiesStore[index],
    };
  },

  async fetchAllUsers(companyId: string): Promise<CompanyUser[]> {
    await delay(300);
    const companyUserIds = (_companyUsersStore[companyId] ?? []).map((u) => u.id);
    return mockAllUsers.filter(
      (u) => isCompanyUserAvailable(u) && !companyUserIds.includes(u.id)
    );
  },

  async fetchAllUsersPaged(
    companyId: string,
    page: number,
    pageSize: number,
    search = '',
    _signal?: AbortSignal
  ): Promise<{ items: CompanyUser[]; pagination: PaginationMetadata }> {
    await delay(300);
    const companyUserIds = (_companyUsersStore[companyId] ?? []).map((u) => u.id);
    const query = search.trim().toLowerCase();
    const filtered = mockAllUsers.filter(
      (user) =>
        isCompanyUserAvailable(user) &&
        !companyUserIds.includes(user.id) &&
        (!query ||
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          user.role.toLowerCase().includes(query))
    );
    return toPagedUsers(filtered, page, pageSize);
  },

  async getApiSecret(
    _companyId: string,
    _type: 'hr' | 'project' | 'timesheet'
  ): Promise<{ maskedApiSecret: string | null }> {
    await delay(200);
    return { maskedApiSecret: null };
  },

  async saveApiSecret(
    _companyId: string,
    _type: 'hr' | 'project' | 'timesheet',
    _apiSecret: string
  ): Promise<{ maskedApiSecret: string | null }> {
    await delay(400);
    return { maskedApiSecret: '****••••••••' };
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async deleteApiSecret(
    _companyId: string,
    _type: 'hr' | 'project' | 'timesheet'
  ): Promise<void> {
    await delay(300);
  },
};
